import { ClientLoaderFunctionArgs, useLoaderData } from "react-router";
import CustomError from "@components/Error/CustomError";
import { MetaDescriptorProps } from "@features/seo/utils/types";
import type { Route } from "@route-types/Hub/+types/HubPage";
import { fetchHubPageData } from "@routes/Hub/HubPage.server";
import HubPageContent from "@routes/Hub/HubPageContent";
import { HubPageContext } from "@routes/Hub/utils/types";
import { fetchContext, ResponseType } from "@sphtech/web2-core/ssr";
import { createMetaTags } from "@sphtech/web2-seo-schema";
import { HttpStatusCode } from "axios";

const HubPage = (): React.ReactElement => {
  const context = useLoaderData<HubPageContext>();

  // StatusCode is not 200
  if (context && context.statusCode !== HttpStatusCode.Ok) {
    return <CustomError statusCode={context.statusCode} />;
  }

  // Check if payload is missing
  if (typeof context?.payload === "undefined") {
    return <CustomError statusCode={HttpStatusCode.NotFound} />;
  }

  return (
    <HubPageContent
      {...context.payload.data}
      gaProps={context.payload.gaProps}
    />
  );
};

export default HubPage;

export const loader = async () => await fetchHubPageData();

export const meta = ({ loaderData }: Route.MetaArgs): MetaDescriptorProps[] => {
  const context = loaderData as HubPageContext;
  if (context.type !== ResponseType.SUCCESS) return [];

  const data = context.payload;

  if (!data) return [];
  if (!data.metaTags) return [];

  return createMetaTags(data.metaTags);
};

export const clientLoader = async (args: ClientLoaderFunctionArgs) => {
  const result = (await fetchContext(args)) as { context?: HubPageContext };

  if (!result.context) return await args.serverLoader();

  return result.context;
};
